import { useContext, useRef, useState } from "react";
import { DataContext } from "../../contexts/DataProvider";
import { useAlert } from "../../functions/useAlert";
import {
  fetchOntologyPreview,
  fetchGlobalOntology,
  fetchOrgOntology,
  saveGlobalOntology,
  saveOrgOntologyFile,
} from "../../services/ontology.service";

interface UseOntologyOptions {
  selectedOrganization: string;
}

export function useOntology({ selectedOrganization }: UseOntologyOptions) {
  const { login }: any = useContext(DataContext);
  const [setAlert] = useAlert();

  const [ontologyTabValue, setOntologyTabValue] = useState(0);
  const [ontologyFileContent, setOntologyFileContent] = useState("");
  const [shaclFileContent, setShaclFileContent] = useState("");
  const [lastSavedOntology, setLastSavedOntology] = useState<string>("");
  const [ontologyMeta, setOntologyMeta] = useState<any>(null);
  const [shaclMeta, setShaclMeta] = useState<any>(null);
  const [lastSavedShacl, setLastSavedShacl] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState<
    "ontology" | "shacl" | null
  >(null);

  const ontologyInputRef = useRef<HTMLInputElement>(null);
  const shaclInputRef = useRef<HTMLInputElement>(null);

  const ontologyHasUnsavedChanges = ontologyFileContent !== lastSavedOntology;
  const shaclHasUnsavedChanges = shaclFileContent !== lastSavedShacl;
  const currentTabHasUnsavedChanges =
    ontologyTabValue === 0 ? ontologyHasUnsavedChanges : shaclHasUnsavedChanges;

  const fetchGraphData = async (type: "ontology" | "shacl") => {
    try {
      setIsFetching(true);
      const response: any = await fetchOntologyPreview(type);
      if (response && typeof response.content === "string") {
        if (type === "ontology") {
          setOntologyFileContent(response.content);
          setLastSavedOntology(response.content);
        } else {
          setShaclFileContent(response.content);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${type} preview:`, error);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchGraphDbOntology = async () => {
    try {
      setIsFetching(true);
      const orgId =
        selectedOrganization && selectedOrganization !== "All Organisation"
          ? selectedOrganization
          : null;
      const resp: any = await fetchGlobalOntology(orgId);
      if (resp?.ttl && typeof resp.ttl === "string" && resp.ttl.trim()) {
        setOntologyFileContent(resp.ttl);
        setLastSavedOntology(resp.ttl);
      }
    } catch (err) {
      console.error("GET /ontology (Python) failed:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchOntologyData = async (orgId?: string) => {
    const targetOrg = orgId || selectedOrganization;
    if (!targetOrg || targetOrg === "All Organisation") return;

    try {
      setIsFetching(true);
      setOntologyFileContent("");
      setShaclFileContent("");
      setLastSavedOntology("");
      setLastSavedShacl("");
      setOntologyMeta(null);
      setShaclMeta(null);

      const response: any = await fetchOrgOntology(targetOrg);

      if (response?.ontology?.content) {
        setOntologyFileContent(response.ontology.content);
        setLastSavedOntology(response.ontology.content);
        setOntologyMeta(response.ontology);
      }
      if (response?.shacl?.content) {
        setShaclFileContent(response.shacl.content);
        setLastSavedShacl(response.shacl.content);
        setShaclMeta(response.shacl);
      }
    } catch (error) {
      console.error("Error fetching ontology data:", error);
      setOntologyFileContent("");
      setShaclFileContent("");
    } finally {
      setIsFetching(false);
    }
  };

  const handleSaveOntology = async () => {
    const content =
      ontologyTabValue === 0 ? ontologyFileContent : shaclFileContent;
    if (!content) {
      setAlert("Nothing to save.", "warning");
      return;
    }
    try {
      setIsSaving(true);
      const orgId =
        selectedOrganization && selectedOrganization !== "All Organisation"
          ? selectedOrganization
          : null;
      await saveGlobalOntology({ ttl: content, org_id: orgId });
      setLastSavedOntology(content);
      setAlert("Ontology saved to GraphDB successfully!", "success");
      setIsEditing(false);
    } catch (error) {
      console.error("PUT /ontology (Python) failed:", error);
      setAlert("Failed to save ontology", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "ontology" | "shacl",
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      if (type === "ontology") {
        setOntologyFileContent(text);
      } else {
        setShaclFileContent(text);
      }

      try {
        setUploadingType(type);
        const userId = login?.data?.id || login?.data?._id;
        const res: any = await saveOrgOntologyFile(
          selectedOrganization,
          type,
          { content: text, orgId: selectedOrganization, userId },
        );
        if (type === "ontology") {
          setLastSavedOntology(text);
          setOntologyMeta(res?.record ?? null);
        } else {
          setLastSavedShacl(text);
          setShaclMeta(res?.record ?? null);
        }
        setAlert(
          `${type === "ontology" ? "Ontology" : "SHACL"} uploaded and saved successfully`,
          "success",
        );
      } catch (error: any) {
        setAlert(
          `Uploaded but failed to save ${type} — please click Save manually`,
          "warning",
        );
      } finally {
        setUploadingType(null);
        event.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  return {
    ontologyTabValue,
    setOntologyTabValue,
    ontologyFileContent,
    setOntologyFileContent,
    shaclFileContent,
    setShaclFileContent,
    lastSavedOntology,
    ontologyMeta,
    shaclMeta,
    lastSavedShacl,
    isEditing,
    setIsEditing,
    isFetching,
    isSaving,
    uploadingType,
    ontologyInputRef,
    shaclInputRef,
    ontologyHasUnsavedChanges,
    shaclHasUnsavedChanges,
    currentTabHasUnsavedChanges,
    fetchGraphData,
    fetchGraphDbOntology,
    fetchOntologyData,
    handleSaveOntology,
    handleFileUpload,
  };
}
